import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { moneyDisplay } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DocLetterhead } from "@/modules/docs/print/DocLetterhead";
import { printDocSlip } from "@/modules/docs/print/DocSlip";
import { useOrgLetterhead } from "@/modules/docs/print/use-org-letterhead";
import { DeskDocBackLink } from "@/modules/shell/DeskDocBackLink";
import { FieldGroup } from "@/modules/shell/FieldGroup";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { getPartyStatement } from "@/shared/api/parties";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";

const AGING_ORDER = [
  "current",
  "d1_30",
  "d31_60",
  "d61_90",
  "d90_plus",
] as const;

export function ContactStatementPage() {
  const { companySlug, partyId } = useParams();
  const [search, setSearch] = useSearchParams();
  const { letterhead } = useOrgLetterhead(companySlug);
  const asOfParam = search.get("asOf") ?? "";
  const sideParam = search.get("side");
  const side =
    sideParam === "ar" || sideParam === "ap" ? sideParam : undefined;
  const [asOfDraft, setAsOfDraft] = useState(asOfParam);

  const q = useQuery({
    queryKey: [
      "party-statement",
      companySlug,
      partyId,
      asOfParam || "today",
      side ?? "auto",
    ],
    enabled: Boolean(companySlug && partyId),
    queryFn: () =>
      getPartyStatement(companySlug!, partyId!, {
        ...(asOfParam ? { asOf: asOfParam } : {}),
        ...(side ? { side } : {}),
      }),
  });
  const errorMsg = queryErrorMessage(q.error, q.isError);
  useAuthRedirectOnQueryError(errorMsg);

  const statement = q.data?.statement;
  const agingRows = useMemo(() => {
    if (!statement) return [];
    return AGING_ORDER.map((id) => ({
      id,
      label: statement.agingLabels[id] ?? id,
      amount: statement.aging[id],
    }));
  }, [statement]);

  if (!companySlug || !partyId) return null;

  return (
    <PageFrame width="narrow">
      <div className="no-print mb-3">
        <DeskDocBackLink
          listBasePath={`/c/${companySlug}/contacts/${partyId}`}
          listLabel="Contact"
        />
      </div>

      <div className="no-print">
        <PageHeader
          eyebrow="Statement of account"
          title={statement?.party.name ?? "Statement"}
          description={
            statement
              ? `${statement.sideLabel} · as of ${statement.asOf}`
              : "Loading…"
          }
          actions={
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!statement}
              onClick={() => printDocSlip()}
            >
              Print
            </Button>
          }
        />

        <form
          className="mt-4 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const next = new URLSearchParams(search);
            if (asOfDraft.trim()) next.set("asOf", asOfDraft.trim());
            else next.delete("asOf");
            setSearch(next, { replace: true });
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="soa-asof">As of</Label>
            <Input
              id="soa-asof"
              type="date"
              className="w-44"
              value={asOfDraft}
              onChange={(e) => setAsOfDraft(e.target.value)}
            />
          </div>
          {statement?.party && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={statement.side === "ar" ? "default" : "outline"}
                onClick={() => {
                  const next = new URLSearchParams(search);
                  next.set("side", "ar");
                  setSearch(next, { replace: true });
                }}
              >
                Receivable
              </Button>
              <Button
                type="button"
                size="sm"
                variant={statement.side === "ap" ? "default" : "outline"}
                onClick={() => {
                  const next = new URLSearchParams(search);
                  next.set("side", "ap");
                  setSearch(next, { replace: true });
                }}
              >
                Payable
              </Button>
            </div>
          )}
          <Button type="submit" size="sm">
            Update
          </Button>
        </form>

        {errorMsg ? (
          <Alert variant="destructive" className="mt-4">
            {errorMsg}
          </Alert>
        ) : null}
      </div>

      {statement ? (
        <article className="pos-receipt mt-6 font-mono text-[12px] leading-snug text-black">
          {letterhead ? <DocLetterhead letterhead={letterhead} /> : null}
          <header className="text-center">
            <div className="text-[11px] uppercase tracking-wider">
              Statement of account
            </div>
            <div className="mt-1 text-[14px] font-bold tracking-wide">
              {statement.party.name}
            </div>
            <div className="mt-1 text-[10px]">{statement.sideLabel}</div>
            <div className="mt-1 text-[10px]">As of {statement.asOf}</div>
            {statement.party.primaryPhone || statement.party.primaryEmail ? (
              <div className="mt-1 space-y-0.5 text-[10px]">
                {statement.party.primaryPhone ? (
                  <div>{statement.party.primaryPhone}</div>
                ) : null}
                {statement.party.primaryEmail ? (
                  <div>{statement.party.primaryEmail}</div>
                ) : null}
              </div>
            ) : null}
            {statement.party.primaryAddress ? (
              <div className="mt-1 text-[10px]">
                {statement.party.primaryAddress}
              </div>
            ) : null}
          </header>

          <div className="my-3 border-t border-dashed border-black/40" />

          <FieldGroup title="Open invoices" level="sub" className="mt-0">
            {statement.lines.length === 0 ? (
              <p className="m-0 text-[11px] text-black/60">No open invoices.</p>
            ) : (
              <ul className="m-0 list-none space-y-2 p-0">
                {statement.lines.map((l) => (
                  <li key={l.id}>
                    <div className="flex justify-between gap-2 font-sans text-[12px] font-medium">
                      <span>{l.number}</span>
                      <span>₱{moneyDisplay(l.balance)}</span>
                    </div>
                    <div className="flex justify-between gap-2 text-[10px]">
                      <span>
                        {l.docDate}
                        {l.paymentDueOn ? ` · due ${l.paymentDueOn}` : ""}
                      </span>
                      <span>
                        orig ₱{moneyDisplay(l.original)} · paid ₱
                        {moneyDisplay(l.paid)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </FieldGroup>

          {statement.credits.length > 0 ? (
            <>
              <div className="my-3 border-t border-dashed border-black/40" />
              <FieldGroup title="Credits / unapplied" level="sub" className="mt-0">
                <ul className="m-0 list-none space-y-2 p-0">
                  {statement.credits.map((c) => (
                    <li key={c.id} className="flex justify-between gap-2">
                      <span>
                        {c.number}
                        {c.postedOn ? ` · ${c.postedOn}` : ""}
                      </span>
                      <span>₱{moneyDisplay(c.unapplied)}</span>
                    </li>
                  ))}
                </ul>
              </FieldGroup>
            </>
          ) : null}

          <div className="my-3 border-t border-dashed border-black/40" />

          <div className="space-y-1">
            <div className="flex justify-between">
              <span>Open total</span>
              <span>₱{moneyDisplay(statement.openTotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>Credits</span>
              <span>₱{moneyDisplay(statement.creditsTotal)}</span>
            </div>
            <div className="flex justify-between text-[13px] font-bold">
              <span>Net amount due</span>
              <span>₱{moneyDisplay(statement.netDue)}</span>
            </div>
          </div>

          <div className="my-3 border-t border-dashed border-black/40" />

          <FieldGroup title="Aging" level="sub" className="mt-0">
            <ul className="m-0 list-none space-y-1 p-0">
              {agingRows.map((r) => (
                <li key={r.id} className="flex justify-between gap-2">
                  <span>{r.label}</span>
                  <span>₱{moneyDisplay(r.amount)}</span>
                </li>
              ))}
            </ul>
          </FieldGroup>

          <footer className="mt-4 text-center text-[10px]">
            Please remit the net amount due. This statement is informational —
            not a tax invoice.
          </footer>
        </article>
      ) : q.isLoading ? (
        <Alert variant="muted" className="mt-4 no-print">
          Loading statement…
        </Alert>
      ) : null}

      <p className="no-print mt-6 text-xs text-[var(--color-muted)]">
        <Link
          to={`/c/${companySlug}/contacts/${partyId}`}
          className="no-underline"
        >
          ← Back to contact
        </Link>
      </p>
    </PageFrame>
  );
}
